import React from 'react';
import { LineChart, Line, ReferenceLine, ResponsiveContainer, YAxis } from 'recharts';
import { TrendingUp } from 'lucide-react';
import type { TrendMetricCriterionData } from './challengeTypes';

const integerFormatter = new Intl.NumberFormat('en-US', { maximumFractionDigits: 0 });
const decimalFormatter = new Intl.NumberFormat('en-US', { maximumFractionDigits: 2 });

const formatValue = (value: number): string =>
  Number.isInteger(value) ? integerFormatter.format(value) : decimalFormatter.format(value);

interface Props {
  data: TrendMetricCriterionData;
}

const TrendMetricCriterion: React.FC<Props> = ({ data }) => {
  const { label, timeframe, baseline, current, target, delta_label, trend } = data;
  const points = (trend ?? []).map((value, index) => ({ index, value }));
  const hasChart = points.length >= 2;

  const numericValues = points.map(p => p.value);
  const dataMin = numericValues.length ? Math.min(...numericValues) : baseline;
  const dataMax = numericValues.length ? Math.max(...numericValues) : target;
  const lo = Math.min(baseline, target, dataMin);
  const hi = Math.max(baseline, target, dataMax);
  const pad = Math.max((hi - lo) * 0.15, 0.05);

  const lastIndex = points.length - 1;

  return (
    <div
      data-testid="challenge-criterion-trend-metric"
      className="rounded-[14px] bg-black/10 p-3 text-white"
    >
      <div className="flex items-baseline justify-between gap-3">
        <span className="text-sm font-bold">{label}</span>
        {timeframe ? (
          <span className="text-xs font-semibold text-white/60">{timeframe}</span>
        ) : null}
      </div>

      <div className="relative mt-3 h-20 w-full">
        <span className="absolute left-1 top-0 z-10 text-[11px] font-semibold text-white/60">
          target {formatValue(target)}
        </span>
        <span className="absolute left-1 bottom-0 z-10 text-[11px] font-semibold text-white/60">
          baseline {formatValue(baseline)}
        </span>

        {hasChart ? (
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={points} margin={{ top: 4, right: 4, left: 4, bottom: 4 }}>
              <YAxis hide domain={[lo - pad, hi + pad]} />
              <ReferenceLine
                y={target}
                stroke="rgba(255,255,255,0.4)"
                strokeDasharray="4 4"
              />
              <ReferenceLine
                y={baseline}
                stroke="rgba(255,255,255,0.4)"
                strokeDasharray="4 4"
              />
              <Line
                type="monotone"
                dataKey="value"
                stroke="#ffffff"
                strokeWidth={2}
                dot={(props: any) =>
                  props.index === lastIndex ? (
                    <circle
                      key={`dot-${props.index}`}
                      cx={props.cx}
                      cy={props.cy}
                      r={2.5}
                      fill="#ffffff"
                    />
                  ) : (
                    <g key={`dot-${props.index}`} />
                  )
                }
                isAnimationActive={false}
              />
            </LineChart>
          </ResponsiveContainer>
        ) : (
          <div className="flex h-full items-center justify-center text-xs text-white/60">
            Not enough data yet
          </div>
        )}
      </div>

      <div className="mt-3 flex items-baseline justify-between gap-3">
        <span className="text-[13px] text-white/60">
          now{' '}
          <span className="font-display text-[18px] font-bold text-white tabular-nums">
            {formatValue(current)}
          </span>
        </span>
        {delta_label ? (
          <span className="inline-flex items-center gap-1 text-xs font-bold text-white/60">
            <TrendingUp size={14} aria-hidden />
            {delta_label}
          </span>
        ) : null}
      </div>
    </div>
  );
};

export default TrendMetricCriterion;
